import { useTranslation } from 'react-i18next';
import Box from '@mui/material/Box';
import Icon from '@mdi/react';
import { mdiArrowLeftThin } from '@mdi/js';
import { useTheme } from '@mui/material';
import Stack from '@mui/material/Stack';
import { FormProvider, useForm, useWatch } from 'react-hook-form';
import Button from '@mui/material/Button';
import Typography from '@mui/material/Typography';
import Container from '@mui/material/Container';
import { useMemo, useState } from 'react';
import { TCustomTheme } from '@mgm/common/theme';
import { useCustomTheme } from '@mgm/common/hooks/useCustomTheme';
import { BaseLink } from '@mgm/common/components/base';
import { NavLink } from 'react-router-dom';
import { BaseColorStepper } from '../../../../components/base/BStepper';
import { useAppDispatch, useAppSelector } from '../../../../hooks/useAppStore';
import { getActiveBulkStep } from '../../../../store/team/selectors';
import { UploadCSV } from '../../../../components/files';
import { BulkMemberFooter } from './BulkMemberFooter';
import { memberActions } from '../../../../store/team/slice';
import { InviteAddMemberNav, InviteTeamList } from '../../../onboarding';
import { getInviteMembers } from '../../../../store/onboarding/selector';
import { AddMembersDialog } from '../../../onboarding/components/AddMembersDialog';
import { BASE_LOCATION } from '../../../../data';

// CSV template content
const csvContent = 'name,email,permission\nJohn Doe,johndoe@example.com,Admin';

const BulkMembers = () => {
  const { t } = useTranslation(['team', 'inputs', 'common']);
  const theme = useTheme() as TCustomTheme;
  const { isMobile } = useCustomTheme(theme);
  const dispatch = useAppDispatch();
  const [mode, setMode] = useState('create');
  const [isOpenDialog, setIsOpenDialog] = useState(false);

  const steps = [t('uploadFile', { ns: 'common' }),
    t('previewAndSubmit', { ns: 'common' })];

  const [formInputs, setFormInputs] = useState({
    locations: BASE_LOCATION,
    permissionLevel: 0,
    firstName: '',
    lastName: '',
    email: '',
  });

  const [isOpenInviteMember, setIsOpenInviteMember] = useState(false);

  const activeStep = useAppSelector(getActiveBulkStep);
  const members = useAppSelector(getInviteMembers);

  const methods = useForm({
    mode: 'all',
  });

  // Watch the value of the "csv" field
  const csvFile = useWatch({
    control: methods.control,
    name: 'csv', // Field name to watch
  });

  // Function to handle CSV download
  const downloadCSVTemplate = () => {
    const element = document.createElement('a');
    const file = new Blob([csvContent], { type: 'text/csv' });
    element.href = URL.createObjectURL(file);
    element.download = 'bulk_upload_template.csv';
    document.body.appendChild(element);
    element.click();
  };

  const handleCloseNav = () => {
    setIsOpenInviteMember(false);
  };

  const handleBack = () => {
    dispatch(memberActions.setActiveBulkStep(activeStep - 1));
  };

  const handleNext = () => {
    if (activeStep !== 1) {
      dispatch(memberActions.setActiveBulkStep(activeStep + 1));
    } else {
      setIsOpenDialog(true);
    }
  };

  const isNextDisabled = useMemo(() => {
    if (activeStep === 0 && !csvFile) return true;

    if (activeStep === 1) {
      // Check for duplicate emails
      const emails = members.map((member) => member.email);
      const hasDuplicateEmails = new Set(emails).size !== emails.length;

      // Check if each member has a valid permissionLevel
      const hasInvalidPermission = members.some((member) => ![0, 1, 2].includes(member.permissionLevel));

      // Disable the next button if there are duplicate emails or invalid permission levels
      return hasDuplicateEmails || hasInvalidPermission;
    }
    return false;
  }, [activeStep, csvFile, members]);

  return (
    <Box>
      <Box
        width="100%"
        {...(!isMobile && {
          display: 'flex',
        })}
        justifyContent="space-between"
      >
        <BaseLink NavLinkComponent={NavLink} to="/team">
          <Box mt={isMobile ? 2 : 0} mb={4} paddingTop={2} display="flex" justifyContent="start">
            <Icon className="mr-1" path={mdiArrowLeftThin} size={0.7} />
            {t('backToTeam')}
          </Box>
        </BaseLink>
        <Box
          {...(isMobile && {
            width: '100%',
            pt: 2,
          })}
          sx={{ display: 'flex', flexDirection: 'column' }}
        >
          <Stack
            sx={{
              width: '100%', flexGrow: 1, justifyContent: 'center', alignItems: 'center',
            }}
            spacing={4}
          >
            <BaseColorStepper activeStep={activeStep} steps={steps} />
          </Stack>
        </Box>
      </Box>
      {activeStep === 0 && (
        <FormProvider {...methods}>
          <Container
            maxWidth="md"
          >
            <Typography pt={2} variant="h6" gutterBottom>
              {t('bulkUploadInstructions')}
            </Typography>
            <Typography variant="body1" paragraph>
              {t('useThisFeatureToUpload')}
            </Typography>
            <Button variant="outlined" onClick={downloadCSVTemplate}>
              {t('downloadCsvTemplate')}
            </Button>
            <Box
              pt={4}
              {...(isMobile && {
                pb: 8,
              })}
            >
              <UploadCSV name="csv" />
            </Box>
          </Container>
        </FormProvider>
      )}
      {activeStep === 1 && (
        <Box pt={3}>
          <InviteTeamList
            isOnboarding={false}
            setMode={setMode}
            setFormInputs={setFormInputs}
            setIsOpenInviteMember={setIsOpenInviteMember}
          />
        </Box>
      )}
      <InviteAddMemberNav
        isOnboarding={false}
        setFormInputs={setFormInputs}
        formInputs={formInputs}
        mode={mode}
        handleCloseNav={handleCloseNav}
        isOpenNav={isOpenInviteMember}
      />
      <BulkMemberFooter
        isNextDisabled={isNextDisabled}
        handleBack={handleBack}
        handleNext={handleNext}
        stepsLength={steps.length}
      />
      <AddMembersDialog
        isOnboarding={false}
        setIsOpenDialog={setIsOpenDialog}
        isOpenDialog={isOpenDialog}
      />
    </Box>
  );
};

export {
  BulkMembers,
};
